import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { Asset, FiatDepositInstructionDto, RowData } from '@api/core';
import { AssetFilterComponent } from '@shared/components/asset-filter/asset-filter.component';
import { BusinessEntitySelectorComponent } from '@shared/components/business-entity-selector/business-entity-selector.component';
import { AutofocusDirective } from '@shared/directives/autofocus.directive';
import { onlyWhitespaceValidator } from '@shared/forms/forms.validators';
import { SharedStore } from '@shared/store/shared.signal-store';
import { trackById } from '@shared/utils/filters';
import { asOptional, isDefined } from '@stillmandigital/common';

interface RowDataForm {
  key: FormControl<string>;
  value: FormControl<string>;
}

@Component({
  selector: 'app-deposit-instruction-dialog',
  templateUrl: './deposit-instruction-dialog.component.html',
  styleUrl: './deposit-instruction-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogClose,
    MatDialogTitle,
    MatIconButton,
    MatIcon,
    MatDialogContent,
    ReactiveFormsModule,
    AutofocusDirective,
    MatFormField,
    MatInput,
    MatLabel,
    MatButton,
    MatDialogActions,
    AssetFilterComponent,
    BusinessEntitySelectorComponent,
  ],
})
export class DepositInstructionDialogComponent {
  public readonly data = inject<FiatDepositInstructionDto | undefined>(MAT_DIALOG_DATA);
  private readonly _fb = inject(NonNullableFormBuilder);
  private readonly _sharedStore = inject(SharedStore);

  public readonly mode: 'Create' | 'Update';

  public readonly assets = this._sharedStore.assets;

  public readonly form = this._fb.group({
    name: ['', Validators.required],
    nameForClient: ['', Validators.required],
    asset: [asOptional<Asset>(), [Validators.required]],
    data: this._fb.array<FormGroup<RowDataForm>>([]),
    businessEntity: [asOptional<FiatDepositInstructionDto.BusinessEntityEnum>(), Validators.required],
  });

  public readonly trackById = trackById;

  constructor() {
    const data = this.data;

    this.mode = data ? 'Update' : 'Create';

    if (data) {
      effect(() => {
        const assets = this.assets();
        this.form.reset({
          name: data.name,
          nameForClient: data.nameForClient,
          asset: assets.find(a => a.id === data.assetId),
          businessEntity: data.businessEntity,
          data: [],
        });

        data.data.forEach(rowData => this.form.controls.data.push(this._getRowDataForm(rowData)));
      });
    } else {
      this.form.reset({
        name: '',
        nameForClient: '',
        asset: undefined,
        businessEntity: undefined,
        data: [],
      });
      this.add();
    }
  }

  public add() {
    const newDataItem = this._getRowDataForm({ key: '', value: '' });
    this.form.controls.data.push(newDataItem);
  }

  public remove(index: number) {
    this.form.controls.data.removeAt(index);
    this.form.markAsDirty();
  }

  public submit() {
    this.form.markAsPristine();
    if (this.mode === 'Create') {
      this._create();
    } else {
      this._update();
    }
  }

  private _create() {
    const { name, nameForClient, asset, data, businessEntity } = this.form.getRawValue();

    if (isDefined(asset?.id) && isDefined(businessEntity)) {
      this._sharedStore.createDepositInstruction({
        name,
        nameForClient,
        assetId: asset.id,
        data,
        businessEntity,
      });
    }
  }

  private _update() {
    const { name, nameForClient, asset, data, businessEntity } = this.form.getRawValue();
    const id = this.data?.id;

    if (isDefined(id) && isDefined(asset?.id) && isDefined(businessEntity)) {
      this._sharedStore.updateDepositInstruction({
        id,
        name,
        nameForClient,
        assetId: asset.id,
        data,
        businessEntity,
      });
    }
  }

  private _getRowDataForm(item: RowData): FormGroup<RowDataForm> {
    return this._fb.group({
      key: [item.key, [Validators.required, onlyWhitespaceValidator()]],
      value: [item.value, [Validators.required, onlyWhitespaceValidator()]],
    });
  }
}
